/**
 * AgentOsSharePage.jsx — public viral landing for a shared achievement.
 *
 * URL: /agent-os/share/:shareId
 * What the visitor sees:
 *   • Big badge emoji + name in the OWNER's brand color
 *   • "Tomáš (brand X) odomkol First Win" + headline stat
 *   • View counter (social proof)
 *   • Strong "Get your own AI agent" CTA → /agent-os/onboard
 *
 * View counter increments once per IP per hour (anti-vanity).
 * No auth required — share_id is a soft public token.
 */
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Bot, ArrowRight, Sparkles, Loader2, Eye, ShoppingCart } from "lucide-react";
import { API } from "../config";

export default function AgentOsSharePage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState({ loading: true, error: null });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [r] = await Promise.all([
          axios.get(`${API}/agent-os/share/${shareId}`),
          axios.post(`${API}/agent-os/share/${shareId}/view`).catch(() => null),
        ]);
        if (alive) { setData(r.data); setStatus({ loading: false, error: null }); }
      } catch (e) {
        if (alive) setStatus({
          loading: false,
          error: e?.response?.status === 404 ? "Tento share link už nie je dostupný." : "Skús to obnoviť.",
        });
      }
    };
    load();
    return () => { alive = false; };
  }, [shareId]);

  // Best-effort dynamic <head> meta (modern scrapers render JS — FB/LI
  // are partially compliant, X is not. Treat as nice-to-have).
  useEffect(() => {
    if (!data) return;
    document.title = `${data.brand_name} · ${data.badge?.name || "Agent OS"}`;
    const setMeta = (prop, content) => {
      let el = document.querySelector(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("og:title", `${data.brand_name} · ${data.badge?.name || "Agent OS"}`);
    setMeta("og:description", data.badge?.share_template_sk || `${data.brand_name} používa TopRankPulse Agent OS.`);
    setMeta("og:type", "website");
  }, [data]);

  if (status.loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center" data-testid="agent-os-share-loading">
        <Loader2 className="w-10 h-10 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (status.error || !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6" data-testid="agent-os-share-error">
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-black mb-3">Hups</h1>
          <p className="text-base text-gray-400 mb-6">{status.error}</p>
          <a href="/agent-os/eshop" className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-400 hover:bg-emerald-300 text-black font-black uppercase tracking-widest text-sm">
            Pozri Agent OS <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  const accent = data.brand_color || "#34d399";
  const badge = data.badge;
  const headlineReplies = data.headline_stat?.replies || 0;
  const headlineSaved = data.headline_stat?.eur_saved || 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="agent-os-share-page">
      <header className="border-b border-gray-900 bg-black/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center" style={{ backgroundColor: accent }}>
              <Bot className="w-5 h-5 text-black" />
            </div>
            <span className="font-black tracking-tight">TopRankPulse <span className="text-gray-500">· Agent OS</span></span>
          </a>
          <a
            href={`/agent-os/onboard?ref=${encodeURIComponent(shareId || "")}`}
            data-testid="agent-os-share-topbar-cta"
            className="px-4 py-2 bg-emerald-400 hover:bg-emerald-300 text-black text-xs font-black uppercase tracking-widest"
          >
            Spusti svojho agenta →
          </a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-3" style={{ color: accent }}>
            {data.brand_name} práve odomkol
          </div>
          <div className="text-9xl mb-6 leading-none">{badge?.emoji || "🎯"}</div>
          <h1 className="text-5xl sm:text-6xl font-black mb-4">{badge?.name || "First Win"}</h1>
          <p className="text-lg text-gray-300 max-w-xl mx-auto">
            {badge?.share_template_sk || `${data.brand_name} používa AI agenta.`}
          </p>
        </div>

        {(headlineReplies > 0 || headlineSaved > 0) && (
          <div className="grid grid-cols-2 gap-4 mb-12" data-testid="agent-os-share-stats">
            <StatTile label="AI odpovedí" value={headlineReplies.toLocaleString("sk-SK")} accent={accent} testid="agent-os-share-stat-replies" />
            <StatTile label="€ ušetrené" value={`€${headlineSaved.toLocaleString("sk-SK")}`} accent={accent} testid="agent-os-share-stat-saved" />
          </div>
        )}

        <div className="p-8 border-2 text-center" style={{ borderColor: accent + "60", background: `linear-gradient(135deg, ${accent}15, #0a0a0a)` }}>
          <ShoppingCart className="w-10 h-10 mx-auto mb-3" style={{ color: accent }} />
          <h2 className="text-2xl sm:text-3xl font-black mb-3">
            Aj tvoj e-shop môže mať <span style={{ color: accent }}>AI agenta</span>.
          </h2>
          <p className="text-sm text-gray-400 mb-6 max-w-xl mx-auto">
            5-minútový setup. 14 dní zadarmo bez karty. 26 jazykov. 24/7.
          </p>
          <a
            href={`/agent-os/onboard?ref=${encodeURIComponent(shareId || "")}`}
            data-testid="agent-os-share-cta"
            className="inline-flex items-center gap-2 px-7 py-3 text-black font-black uppercase tracking-widest text-sm"
            style={{ backgroundColor: accent }}
          >
            <Sparkles className="w-4 h-4" /> Aktivuj môjho prvého agenta <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-500" data-testid="agent-os-share-views">
          <Eye className="w-3 h-3" />
          <span className="tabular-nums">{(data.view_count || 0).toLocaleString("sk-SK")}</span>
          <span>zobrazení</span>
        </div>
      </main>

      <footer className="border-t border-gray-900 py-6 text-center text-xs text-gray-600">
        TopRankPulse · Agent OS
      </footer>
    </div>
  );
}

function StatTile({ label, value, accent, testid }) {
  return (
    <div className="p-5 bg-black border-2" style={{ borderColor: accent + "40" }} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-2">{label}</div>
      <div className="text-4xl font-black tabular-nums" style={{ color: accent }}>{value}</div>
    </div>
  );
}
